import angular from 'angular'
import inspect from 'object-inspect'

import idlize from '../utils/idlize'
import MapSimple from '../utils/map-simple'
import pageScroll from '../utils/page-scroll'
import extractRawElm from '../utils/extract-raw-elm'
import ResizeDetector from '../utils/resize-detector'
import viewportResize from '../utils/viewport-resize'
import TransformDetector from './transform-detector'

import {
  noop, wrap, pick, first,
  assign, property, includes,
  zipObject, isFunction, upperFirst
} from 'lodash-es'

import { defaultTo } from '../utils/bits'
import { getElmStrShort } from '../utils/get-elm-str'

angular
  .module('ggs.element-position')
  .factory('ElementPositionInternal', ElementPositionInternalProvider)
  .factory('ElementPosition', ElementPositionProvider)

/*
  ex 1:    _____ELM______   ______
          |              |        |
         _|_____VP_______|____  __| topOffset = -200px
        | |              |    |
        | |______________|  __|___
        |_____________________|  _| bottomOffset = 100px

  ex 2:  _______VP____________  __
        |                     |   |
        |                     |   |
        |  _____ELM____   ____|___| topOffset = 300px
        |_|____________|______|  _
          |            |          |
          |____________|  ________| bottomOffset = -200px
*/

const INTERVAL_DELAY = 1000 // ms
const INTERVAL_INF_LOOP = 0
const INTERVAL_SKIP_APPLY = false

const FLAGS = [
  'viewportWidth',
  'viewportHeight',
  'top',
  'bottom',
  'width',
  'height',
  'topOffset',
  'bottomOffset',
  'absTopOffset',
  'absBottomOffset',
  'displayed',
  'inViewport',
  'fitViewport',
  'fillViewport',
  'topInViewport',
  'bottomInViewport',
  'topAboveViewport',
  'topBelowViewport',
  'bottomAboveViewport',
  'bottomBelowViewport'
]

function ElementPositionInternalProvider(
  $interval,
  $rootScope,
  DisplayDetector) {

  let gearsId = 0
  const nextGearsId = () => gearsId++

  let regId = 0
  const nextRegId = () => regId++

  const elm2gears = MapSimple()
  const irecalcAll = idlize(recalcAll)

  pageScroll.on(irecalcAll)
  viewportResize.on(irecalcAll)
  const intervalCheckId = $interval(irecalcAll, INTERVAL_DELAY, INTERVAL_INF_LOOP, INTERVAL_SKIP_APPLY)

  $rootScope.$on('$destroy', onDestroy)

  return assign(register, {
    getStatsList
  })

  function onDestroy() {
    pageScroll.off(irecalcAll)
    viewportResize.off(irecalcAll)
    $interval.cancel(intervalCheckId)
  }

  function getStatsList() {
    return elm2gears.pairs().map(([elm, gears]) => {
      const elmStr = getElmStrShort(elm)
      const elmPos = pick(defaultTo(first(gears.regs).input, {}), 'width', 'height', 'top', 'bottom')
      const gearsId = gears.id
      const regList = gears.regs.map((reg) => ({
        regId: reg.id,
        query: defaultTo(reg.query.info, inspect(reg.query)),
        count: reg.count,
        result: reg.result
      }))

      return { elmStr, elmPos, gearsId, regList }
    })
  }

  function register($elm, query, callback) {
    const elm = extractRawElm($elm)
    const gears = elm2gears.has(elm)
      ? elm2gears.get(elm)
      : elm2gears.set(elm, Gears(elm))

    const reg = {
      id: nextRegId(),
      elm,
      query,
      count: 0,
      input: undefined,
      result: undefined,
      callbacks: isFunction(callback) ? [callback] : []
    }

    reg.attach = wrap(reg, attach)
    reg.detach = wrap(reg, detach)
    reg.destroy = wrap(reg, release)

    gears.regs.push(reg)
    gears.resizeDetector.on(gears.irecalc)
    gears.displayDetector.on(gears.irecalc)
    gears.transformDetector.on(gears.irecalc)
    gears.irecalc()

    return reg
  }

  function Gears(elm) {
    return {
      id: nextGearsId(),
      regs: [],
      irecalc: idlize(wrap(elm, recalc)),
      resizeDetector: ResizeDetector(elm),
      displayDetector: DisplayDetector(elm),
      transformDetector: TransformDetector(elm)
    }
  }

  function attach(reg, callback) {
    if (!includes(reg.callbacks, callback)) {
      reg.callbacks.push(callback)
      idlize(InitCallback(reg, callback))
    }
  }

  function InitCallback(reg, callback) {
    const last = reg.count
    return function initCallback() {
      if (reg.count === last && reg.input && includes(reg.callbacks, callback)) {
        callback(reg.result)
      }
    }
  }

  function detach(reg, callback) {
    const index = reg.callbacks.indexOf(callback)
    if (index !== -1) {
      reg.callbacks.splice(index, 1)
    }
  }

  function release(reg) {
    const elm = reg.elm
    const gears = elm2gears.get(elm)
    if (!gears) {
      return
    }

    const index = gears.regs.indexOf(reg)

    reg.count = Infinity
    reg.input = undefined
    reg.result = undefined
    reg.callbacks = []

    reg.query = noop
    reg.attach = noop
    reg.detach = noop
    reg.destroy = noop

    if (index !== -1) {
      gears.regs.splice(index, 1)
    }

    if (gears.regs.length === 0) {
      gears.resizeDetector.off(gears.irecalc)
      gears.displayDetector.off(gears.irecalc)
      gears.transformDetector.off(gears.irecalc)
      gears.irecalc = noop

      elm2gears.del(elm)
    }
  }

  function recalcAll() {
    const elms = elm2gears.keys()
    for (const elm of elms) {
      recalc(elm)
    }
  }

  function recalc(elm) {
    const gears = elm2gears.get(elm)
    if (!gears) {
      return
    }

    const input = Input(elm)
    const localRegs = gears.regs.concat()
    for (const reg of localRegs) {
      const result = reg.query(input)
      reg.input = input
      if (reg.result === result) {
        continue
      }

      reg.count++
      reg.result = result

      const localCallbacks = reg.callbacks.concat()
      for (const callback of localCallbacks) {
        callback(result)
      }
    }
  }

  function Input(elm) {
    const rect = elm.getBoundingClientRect()

    const viewportWidth = viewportResize.getWidth()
    const viewportHeight = viewportResize.getHeight()

    const top = Math.round(rect.top)
    const bottom = Math.round(rect.bottom)

    const width = Math.round(rect.width)
    const height = Math.round(rect.height)

    const topOffset = Math.round(rect.top)
    const bottomOffset = viewportHeight - Math.round(rect.bottom)

    const absTopOffset = Math.abs(topOffset)
    const absBottomOffset = Math.abs(bottomOffset)

    const displayed = (width !== 0) && (height !== 0)

    const inViewport = (topOffset < viewportHeight) && (bottomOffset < viewportHeight)
    const fitViewport = (topOffset >= 0) && (bottomOffset >= 0)
    const fillViewport = (topOffset <= 0) && (bottomOffset <= 0)

    const topInViewport = (topOffset >= 0) && (topOffset < viewportHeight)
    const bottomInViewport = (bottomOffset >= 0) && (bottomOffset < viewportHeight)

    const topAboveViewport = topOffset < 0
    const topBelowViewport = topOffset >= viewportHeight

    const bottomAboveViewport = bottomOffset >= viewportHeight
    const bottomBelowViewport = bottomOffset < 0

    const calcFillViewportPart = (fraction) => {
      const limit = viewportHeight * (1 - fraction)
      return false
        || (topOffset <= 0) && (bottomOffset <= limit)
        || (topOffset <= limit) && (bottomOffset <= 0)
    }

    const input = {
      viewportWidth,
      viewportHeight,

      top,
      bottom,

      width,
      height,

      topOffset,
      bottomOffset,

      absTopOffset,
      absBottomOffset,

      displayed,

      inViewport,
      fitViewport,
      fillViewport,

      topInViewport,
      bottomInViewport,

      topAboveViewport,
      topBelowViewport,

      bottomAboveViewport,
      bottomBelowViewport,

      calcFillViewportPart
    }

    return input
  }
}

function ElementPositionProvider(
  $rootScope,
  ApplyAsync,
  ElementPositionInternal) {

  const shorthands = zipObject(
    FLAGS.map(upperFirst),
    FLAGS.map(Shorthand))

  assign(ElementPosition, shorthands)

  return ElementPosition

  function ElementPosition($elm, query, callback) {
    const map = MapSimple()
    let asyncCallback = undefined
    if (isFunction(callback)) {
      asyncCallback = ApplyAsync($rootScope, callback)
      map.set(callback, asyncCallback)
    }

    const reg = ElementPositionInternal($elm, query, asyncCallback)
    const origAttach = reg.attach
    const origDetach = reg.detach

    reg.attach = wrapAttach
    reg.detach = wrapDetach

    return reg

    function wrapAttach(callback) {
      if (!map.has(callback)) {
        const asyncCallback = ApplyAsync($rootScope, callback)
        map.set(callback, asyncCallback)
        return origAttach(asyncCallback)
      }
    }

    function wrapDetach(callback) {
      if (map.has(callback)) {
        const asyncCallback = map.get(callback)
        map.del(callback)
        return origDetach(asyncCallback)
      }
    }
  }

  function Shorthand(flag) {
    return function shorthand($elm, callback) {
      const query = assign(property(flag), { info: `[Shorthand: ${flag}]` })
      return ElementPosition($elm, query, callback)
    }
  }
}
